import { useEffect, useMemo } from "react"
import Link from "next/link"
import { useRouter } from "next/router"

import { Identity } from "@apptivity-lab/firmament-node-sdk"
import { useMachine } from "@lib"

import { AuthVerificationRequestStateMachine } from "@machines/authentication/AuthVerificationRequestStateMachine"

import LoginForm from "@components/authentication/LoginForm"
import AFFAuthPageLayout from "@components/domain/AFFAuthPageLayout"

const Content: React.FunctionComponent<{
    disabled?: boolean,
    error?: Error
    returnUrl?: string,
    onSubmit: (identity: Identity) => void
}> = ({ disabled, error, returnUrl, onSubmit }) => (
    <>
        <h1 className="text-title1 md:text-large-title text-black-900">Log In</h1>
        <LoginForm className="mt-[32px]"
            disabled={disabled}
            error={error}
            onSubmit={onSubmit} />

        <div className="mt-[32px] text-center">
            <div className="text-black-700 text-subhead mb-[8px]">Are you a community member?</div>
            <Link href={`/auth/community-login?return=${returnUrl || ""}`}>
                <a className="text-link text-callout uppercase">Community Log In</a>
            </Link>
        </div>

        <hr className="border-black-200 mt-[32px]" />

        <div className="mt-[32px] text-center">
            <div className="text-black-700 text-subhead mb-[8px]">New here?</div>
            <Link href="/auth/register">
                <a className="text-link text-callout uppercase">Register an account</a>
            </Link>
        </div>
    </>
)

function LoginPage() {
    const router = useRouter()
    const returnUrl = useMemo(() => {
        const returnPath = router.query.return
        if (returnPath && typeof returnPath === "string") {
            if (localStorage) {
                localStorage.setItem("afterAuthReturn", returnPath)
            }

            return returnPath
        } else {
            return undefined
        }
    }, [router])

    const [machine, machineState] = useMachine(AuthVerificationRequestStateMachine)

    useEffect(() => {
        machine.start()
    }, [machine])

    switch (machineState.name) {
        case "completed":
            if (machineState.identity?.identifier) {
                router.replace(`/identities/${machineState.identity?.identifier}/verify?return=${returnUrl || ""}`)
            } else {
                router.replace(returnUrl || "/")
            }
            return <Content disabled={true} returnUrl={returnUrl} onSubmit={(identity) => machine.requestVerificationCode(identity)} />
        case "showingError":
            return <Content error={machineState.error} returnUrl={returnUrl} onSubmit={(identity) => machine.requestVerificationCode(identity)} />
        default:
            return <Content disabled={machineState.name === "requestingCode"} returnUrl={returnUrl} onSubmit={(identity) => machine.requestVerificationCode(identity)} />
    }
}

LoginPage.Layout = AFFAuthPageLayout

export default LoginPage
